import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner, PageError } from '@/components/ui';
import { useTranslation } from 'react-i18next';

interface KPICard {
  title: string;
  value: number | string;
  subtitle: string;
  icon: JSX.Element;
  color: 'yellow' | 'green' | 'orange' | 'red' | 'blue';
  onClick: () => void;
  badge?: string;
}

const IconThermometer = () => (
  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const IconAlertTriangle = () => (
  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconBroom = () => (
  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);
const IconOilDrop = () => (
  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const IconClock = () => (
  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 2m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconBell = () => (
  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);
const IconCreditCard = () => (
  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1M7 11h1m4 0h1M3 6h18a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2z" />
  </svg>
);
const IconCheck = () => (
  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

function KPICard({ title, value, subtitle, icon, color, onClick, badge }: KPICard) {
  const colorConfig = {
    yellow: { bg: 'bg-gradient-to-br from-white to-accent-yellow/5 border-l-4 border-accent-yellow', border: 'border-accent-yellow/20', iconBg: 'bg-gradient-to-br from-accent-yellow/20 to-accent-yellow/10', textColor: 'text-neutral-900', badgeColor: 'bg-accent-yellow text-white', shadowColor: 'shadow-lg hover:shadow-xl hover:shadow-accent-yellow/20' },
    green: { bg: 'bg-gradient-to-br from-white to-accent-green/5 border-l-4 border-accent-green', border: 'border-accent-green/20', iconBg: 'bg-gradient-to-br from-accent-green/20 to-accent-green/10', textColor: 'text-neutral-900', badgeColor: 'bg-accent-green text-white', shadowColor: 'shadow-lg hover:shadow-xl hover:shadow-accent-green/20' },
    orange: { bg: 'bg-gradient-to-br from-white to-accent-orange/5 border-l-4 border-accent-orange', border: 'border-accent-orange/20', iconBg: 'bg-gradient-to-br from-accent-orange/20 to-accent-orange/10', textColor: 'text-neutral-900', badgeColor: 'bg-accent-orange text-white', shadowColor: 'shadow-lg hover:shadow-xl hover:shadow-accent-orange/20' },
    red: { bg: 'bg-gradient-to-br from-white to-accent-red/5 border-l-4 border-accent-red', border: 'border-accent-red/20', iconBg: 'bg-gradient-to-br from-accent-red/20 to-accent-red/10', textColor: 'text-neutral-900', badgeColor: 'bg-accent-red text-white', shadowColor: 'shadow-lg hover:shadow-xl hover:shadow-accent-red/20' },
    blue: { bg: 'bg-gradient-to-br from-white to-primary-100/30 border-l-4 border-primary-500', border: 'border-primary-200', iconBg: 'bg-gradient-to-br from-primary-100 to-primary-50', textColor: 'text-neutral-900', badgeColor: 'bg-gradient-primary text-white', shadowColor: 'shadow-lg hover:shadow-xl hover:shadow-primary-500/20' },
  };
  const config = colorConfig[color];

  return (
    <div onClick={onClick} className={`${config.bg} rounded-xl p-4 sm:p-6 cursor-pointer ${config.shadowColor} hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)'}} />
      <div className="flex items-start justify-between relative z-10">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`text-sm font-bold ${config.textColor}`}>{title}</h3>
            {badge && <span className={`${config.badgeColor} text-xs font-bold px-2.5 py-1 rounded-full`}>{badge}</span>}
          </div>
          <p className={`text-3xl sm:text-4xl font-bold ${config.textColor} my-3 transition-colors duration-300`}>{value}</p>
          <p className="text-xs sm:text-sm text-neutral-600">{subtitle}</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className={`${config.iconBg} w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md group-hover:shadow-lg`}>
            {icon}
          </div>
          <svg className="w-5 h-5 text-neutral-400 group-hover:text-neutral-600 group-hover:translate-x-0.5 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const REFRESH_INTERVAL = 30 * 1000;

  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      try { return await apiClient.getDashboardSummary(); }
      catch { return { equipmentActiveCount: 0, openIncidentsCount: 0, unreadNotifications: 0 }; }
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      try { return await apiClient.getSubscription(); }
      catch { return null; }
    },
    refetchInterval: REFRESH_INTERVAL * 2,
  });

  const { data: cleaningToday } = useQuery({
    queryKey: ['cleaning-today'],
    queryFn: async () => {
      try { return await apiClient.getCleaningToday(); }
      catch { return null; }
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: temperatureLogs } = useQuery({
    queryKey: ['temperature-logs-today'],
    queryFn: async () => {
      try { return await apiClient.getTemperatureLogs(0, 100); }
      catch { return { data: [], total: 0, skip: 0, take: 100 }; }
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: oilStationsData } = useQuery({
    queryKey: ['oil-stations'],
    queryFn: async () => {
      try { return await apiClient.getOilStations(); }
      catch { return { stations: [] }; }
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const oilStations = oilStationsData?.stations || [];

  if (dashboardLoading) return <LoadingSpinner message={t('dashboard.loading')} />;
  if (dashboardError) return <PageError message={t('dashboard.error')} />;

  const openIncidentsCount = dashboard?.openIncidentsCount || 0;
  const equipmentNeedingReadings = (dashboard as any)?.equipmentNeedingReadings || 0;
  const equipmentOverdue = (dashboard as any)?.equipmentOverdue || 0;
  const unreadNotifications = dashboard?.unreadNotifications || 0;
  const subscription_status = subscription?.status || 'INACTIVE';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayLogs = (temperatureLogs?.data || []).filter((log: any) => {
    const logDate = new Date(log.measuredAt || log.recordedAt);
    logDate.setHours(0, 0, 0, 0);
    return logDate.getTime() === today.getTime();
  });
  const outOfRangeLogs = todayLogs.filter((log: any) => log.conformity === 'OUT_OF_RANGE' || log.isOutOfRange).length;
  const cleaningCompleted = !!cleaningToday?.plan?.completedAt;
  const todayOilChecks = oilStations.filter((station: any) => {
    if (!station.lastCheck?.checkedAt && !station.lastCheck?.createdAt) return false;
    const checkDate = new Date(station.lastCheck.checkedAt || station.lastCheck.createdAt);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate.getTime() === today.getTime();
  }).length;

  const topCards: KPICard[] = [
    {
      title: t('dashboard.temperaturesToEnter'),
      value: equipmentNeedingReadings,
      subtitle: equipmentNeedingReadings > 0
        ? (equipmentOverdue > 0 ? t('dashboard.overdueReadings', { count: equipmentOverdue }) : t('dashboard.pendingReadings'))
        : t('dashboard.allReadings'),
      icon: <IconThermometer />,
      color: equipmentOverdue > 0 ? 'red' : equipmentNeedingReadings > 0 ? 'yellow' : 'green',
      onClick: () => navigate('/temperature-logs'),
      badge: equipmentNeedingReadings > 0 ? `${equipmentNeedingReadings}` : undefined,
    },
    {
      title: t('dashboard.temperaturesOutOfRange'),
      value: outOfRangeLogs,
      subtitle: outOfRangeLogs > 0 ? t('dashboard.alertsToday', { count: outOfRangeLogs }) : t('dashboard.allConform'),
      icon: <IconAlertTriangle />,
      color: outOfRangeLogs > 0 ? 'red' : 'green',
      onClick: () => navigate('/temperature-logs'),
      badge: outOfRangeLogs > 0 ? `${outOfRangeLogs}` : undefined,
    },
    {
      title: t('dashboard.cleaningToday'),
      value: cleaningCompleted ? t('dashboard.done') : t('dashboard.todo'),
      subtitle: cleaningCompleted ? t('dashboard.validatedToday') : t('dashboard.pendingValidation'),
      icon: <IconBroom />,
      color: cleaningCompleted ? 'green' : 'yellow',
      onClick: () => navigate('/cleaning'),
    },
  ];

  const bottomCards: KPICard[] = [
    {
      title: t('dashboard.oilControl'),
      value: t('dashboard.checks', { count: todayOilChecks }),
      subtitle: t('dashboard.checksToday', { total: oilStations.length }),
      icon: <IconOilDrop />,
      color: todayOilChecks > 0 ? 'green' : 'orange',
      onClick: () => navigate('/oil'),
    },
    {
      title: t('dashboard.openIncidents'),
      value: openIncidentsCount,
      subtitle: openIncidentsCount > 0 ? t('dashboard.incidentsToProcess', { count: openIncidentsCount }) : t('dashboard.noIncidents'),
      icon: <IconClock />,
      color: openIncidentsCount > 0 ? 'red' : 'green',
      onClick: () => navigate('/incidents'),
      badge: openIncidentsCount > 0 ? `${openIncidentsCount}` : undefined,
    },
    {
      title: t('dashboard.notifications'),
      value: unreadNotifications,
      subtitle: unreadNotifications > 0 ? t('dashboard.unreadNotifications', { count: unreadNotifications }) : t('dashboard.allRead'),
      icon: <IconBell />,
      color: unreadNotifications > 0 ? 'blue' : 'green',
      onClick: () => navigate('/settings'),
      badge: unreadNotifications > 0 ? `${unreadNotifications}` : undefined,
    },
  ];

  const statusItems = [
    {
      label: equipmentNeedingReadings > 0 ? t('dashboard.pendingReadingsStatus', { count: equipmentNeedingReadings }) : t('dashboard.readingsUpToDate'),
      icon: equipmentNeedingReadings > 0 ? <IconClock /> : <IconCheck />,
      color: equipmentNeedingReadings > 0 ? 'orange' : 'green',
    },
    {
      label: openIncidentsCount > 0 ? t('dashboard.openIncidentsStatus', { count: openIncidentsCount }) : t('dashboard.noIncidentStatus'),
      icon: openIncidentsCount > 0 ? <IconClock /> : <IconCheck />,
      color: openIncidentsCount > 0 ? 'red' : 'green',
    },
    {
      label: cleaningCompleted ? t('dashboard.cleaningDone') : t('dashboard.cleaningTodo'),
      icon: cleaningCompleted ? <IconCheck /> : <IconClock />,
      color: cleaningCompleted ? 'green' : 'orange',
    },
    {
      label: subscription_status === 'ACTIVE' ? t('dashboard.subscriptionActive') : t('dashboard.subscriptionInactive'),
      icon: <span className={`w-2 h-2 rounded-full ${subscription_status === 'ACTIVE' ? 'bg-green-600' : 'bg-orange-500'} inline-block`} />,
      color: subscription_status === 'ACTIVE' ? 'green' : 'orange',
    },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {topCards.map((card, idx) => (
          <div key={idx} className="animate-fade-in" style={{animationDelay: `${idx * 100}ms`}}>
            <KPICard {...card} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {bottomCards.map((card, idx) => (
          <div key={idx} className="animate-fade-in" style={{animationDelay: `${(idx + 3) * 100}ms`}}>
            <KPICard {...card} />
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-white via-neutral-50 to-brand-soft border border-neutral-200 rounded-xl p-5 sm:p-7 shadow-lg hover:shadow-xl transition-all">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-6 bg-gradient-primary rounded-full" />
          <h2 className="text-xl font-bold text-neutral-900">{t('dashboard.quickStatus')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statusItems.map((item, idx) => {
            const colorMap = {
              green: {bg: 'bg-gradient-to-br from-accent-green/15 to-accent-green/5', border: 'border-accent-green/30', shadow: 'shadow-lg hover:shadow-accent-green/30', text: 'text-accent-green font-semibold'},
              yellow: {bg: 'bg-gradient-to-br from-accent-orange/15 to-accent-orange/5', border: 'border-accent-orange/30', shadow: 'shadow-lg hover:shadow-accent-orange/30', text: 'text-accent-orange font-medium'},
              orange: {bg: 'bg-gradient-to-br from-accent-orange/15 to-accent-orange/5', border: 'border-accent-orange/30', shadow: 'shadow-lg hover:shadow-accent-orange/30', text: 'text-accent-orange font-medium'},
              red: {bg: 'bg-gradient-to-br from-accent-red/15 to-accent-red/5', border: 'border-accent-red/30', shadow: 'shadow-lg hover:shadow-accent-red/30', text: 'text-accent-red font-semibold'},
            };
            const statusColor = colorMap[item.color as keyof typeof colorMap] || colorMap.yellow;
            return (
              <div key={idx} className={`${statusColor.bg} border ${statusColor.border} rounded-lg p-3 sm:p-4 flex items-center gap-3 hover:scale-105 transition-all duration-300 ${statusColor.shadow}`}>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-lg">{item.icon}</div>
                <span className={`text-xs sm:text-sm ${statusColor.text}`}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 border border-neutral-200 rounded-xl p-5 sm:p-7 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-6 bg-gradient-warning rounded-full" />
            <h3 className="text-lg font-bold text-neutral-900">{t('dashboard.quickActions')}</h3>
          </div>
          <div className="space-y-3">
            <button onClick={() => navigate('/temperature-logs')} className="w-full text-left px-4 py-3 rounded-lg bg-gradient-to-r from-accent-yellow/20 to-accent-yellow/10 hover:from-accent-yellow/30 hover:to-accent-yellow/20 text-neutral-900 font-medium text-sm transition-all duration-300 border border-accent-yellow/40 hover:border-accent-yellow/60 hover:shadow-lg hover:shadow-accent-yellow/20 hover:scale-105">
              {t('dashboard.enterTemperature')}
            </button>
            <button onClick={() => navigate('/cleaning')} className="w-full text-left px-4 py-3 rounded-lg bg-gradient-to-r from-accent-green/20 to-accent-green/10 hover:from-accent-green/30 hover:to-accent-green/20 text-neutral-900 font-medium text-sm transition-all duration-300 border border-accent-green/40 hover:border-accent-green/60 hover:shadow-lg hover:shadow-accent-green/20 hover:scale-105">
              {t('dashboard.recordCleaning')}
            </button>
            <button onClick={() => navigate('/incidents')} className="w-full text-left px-4 py-3 rounded-lg bg-gradient-to-r from-accent-orange/20 to-accent-orange/10 hover:from-accent-orange/30 hover:to-accent-orange/20 text-neutral-900 font-medium text-sm transition-all duration-300 border border-accent-orange/40 hover:border-accent-orange/60 hover:shadow-lg hover:shadow-accent-orange/20 hover:scale-105">
              {t('dashboard.reportIncident')}
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary-50 to-primary-100/30 border border-primary-200 rounded-xl p-5 sm:p-7 shadow-card hover:shadow-card-hover transition-all">
          <div className="flex items-center gap-3 mb-4">
            <IconCreditCard />
            <h3 className="text-lg font-bold text-primary-900">{t('dashboard.subscription')}</h3>
          </div>
          <div className="mb-5 p-4 bg-white/80 rounded-lg border border-primary-300">
            <p className="text-sm text-primary-700">
              {t('dashboard.subscriptionStatus', { status: subscription_status })}
            </p>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="w-full px-4 py-3 rounded-lg bg-gradient-primary text-white font-bold text-sm transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
            {t('dashboard.manageSubscription')}
          </button>
        </div>
      </div>
    </div>
  );
}
